import {useAnalytics} from '@/ts/composables/stateful/use-analytics';
import {commonProperties, taskProperties, TodayEvent} from '@/ts/composables/pure/today-event-names';
import {useI18n} from 'vue-i18n';

const TODAY_TASK_KEY = 'todayTask';

/**
 * The mark, read and written here rather than through the shared `SessionStorage`.
 *
 * Two reasons, and both are about blast radius. The screen that reports is a medical device entry
 * point, so anything it imports enters the manifest's scope, and that file is edited by other live
 * branches. And storage access itself can throw — a browser configured to refuse it does, on the
 * property read, not on the value — while this is analytics attribution sitting in the first
 * statement of a completion screen's `onMounted`. An exception there would take the celebration
 * down with it, so a failure to measure returns nothing and the screen carries on.
 */
function readMark(): string | null {
  try {
    return window.sessionStorage.getItem(TODAY_TASK_KEY);
  } catch {
    return null;
  }
}

function writeMark(value: string | null): void {
  try {
    if (value) {
      window.sessionStorage.setItem(TODAY_TASK_KEY, value);
    } else {
      window.sessionStorage.removeItem(TODAY_TASK_KEY);
    }
  } catch {
    // Measuring is not worth a page, and the mark is only ever read back by this same guard.
  }
}

/** How the Today page names a session task, and what the completed screen has to match against. */
export function todaySessionTaskId(sessionId: string): string {
  return `session:${sessionId}`;
}

/** The two halves of attributing a completed task to the Today page. */
export interface TodayTaskCompletion {
  /** Called from the Today page when a session is opened, before the link navigates. */
  markOpened(taskId: string): void;
  /** Called from the screen that says the task is done. Reports only if this is the marked one. */
  reportCompleted(taskId: string, taskType: string): void;
}

/**
 * Attributes a finished task to the Today page (IPD-2974).
 *
 * The mark travels in the tab's own storage rather than in the URL, because the URL cannot carry
 * it: the query is set on the link out of the Today page, and every link built between the pages
 * of a session drops it, while the completion happens on the last one. `challengeExpired` in the
 * shared session storage carries its signal across a navigation for the same reason.
 *
 * SESSIONS ONLY, for now. One slot holds the mark, and the only screen that reports against it is
 * the session-completed one, so a homework, challenge or assessment mark could never be redeemed
 * and would evict a session mark that could. The page marks nothing else until a screen exists to
 * redeem it.
 *
 * Two things it does not promise, both stated because the shape invites the opposite assumption:
 *
 * - It is not a click-to-completion funnel. The mark is written on the click, but nothing ties it
 *   to that click afterwards — no timestamp and no navigation check — so a session opened from
 *   here, abandoned, and finished later in the same tab through Paths still reports as this page's.
 *   Session-scoped is as narrow as it gets: a new tab, or tomorrow, is not attributed.
 * - A click that opens a new tab still writes the mark, and still counts. `sessionStorage` is
 *   cloned into the new tab, so the completion there matches. The original tab keeps a mark it
 *   will not redeem, which the one-slot rule then overwrites on the next click.
 *
 * Cleared on report, so returning to a completed screen later cannot count it twice.
 */
export function useTodayTaskCompletion(): TodayTaskCompletion {
  const {trackEvent} = useAnalytics();
  const {locale} = useI18n();

  return {
    markOpened(taskId: string): void {
      writeMark(taskId);
    },

    reportCompleted(taskId: string, taskType: string): void {
      if (readMark() !== taskId) {
        return;
      }
      writeMark(null);
      trackEvent(TodayEvent.taskCompleted, {
        ...commonProperties(locale.value),
        ...taskProperties(taskType, taskId),
      });
    },
  };
}
